import React, { useState, useEffect, useRef, useCallback } from 'react';
import '../scss/CraneDashboard.scss';
import BaseURL from '../assets/contants/BaseURL';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUserCircle,
  faCaretDown,
  faUser,
  faSyncAlt,
  faSignOutAlt,
  faCrown
} from '@fortawesome/free-solid-svg-icons';

const CraneDashboard = () => {
  const navigate = useNavigate();
  const [craneData, setCraneData] = useState({});
  const [currentFilter, setCurrentFilter] = useState('all');
  const [oeeData, setOeeData] = useState({
    oee_percentage: 0,
    availability_percentage: 0,
    productivity_percentage: 0,
    quality_percentage: 0
  });
  const [summaryData, setSummaryData] = useState({
    motor_breakdown: { total_power_kwh: 0, total_energy_cost: 0 },
    active_cranes: 0,
    idle_cranes: 0,
    total_cranes: 0,
    yesterday: { total_power: 0, total_cost: 0 }
  });
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState({ cranes: [] });
  const [orgLogo, setOrgLogo] = useState(null);

  // WebSocket refs
  const oeeWebSocketRef = useRef(null);
  const analyticsWebSocketRef = useRef(null);
  const craneLastDataTimestampRef = useRef({});

  const CRANE_STATUS = {
    ACTIVE: 'Working',
    WORKING: 'Working',
    IDLE: 'Idle',
    OFF: 'Off',
    OVERLOAD: 'Overload',
    ERROR: 'Error',
    EMERGENCY: 'Emergency'
  };

  // Helper functions
  const getAuthToken = () => sessionStorage.getItem('Token') || localStorage.getItem('Token');
  const getSessionID = () => sessionStorage.getItem('session_id') || localStorage.getItem('session_id');

  useEffect(() => {
    const fetchOrgLogo = async () => {
      try {
        const token = getAuthToken();
        const organizationId = sessionStorage.getItem('organization_id');

        const response = await fetch(`${BaseURL}Userauth/organizations/${organizationId}/`, {
          headers: {
            'Authorization': `Token ${token}`,
            'Content-Type': 'application/json',
          }
        });

        if (response.ok) {
          const data = await response.json();
          if (data.data?.logo_url) { 
            setOrgLogo(data.data.logo_url);
          }
        }
      } catch (error) {
        console.error('Error fetching org logo:', error);
      }
    };

    fetchOrgLogo();
  }, []);

  const formatNumber = (value, decimals = 1) => {
    if (value === undefined || value === null || isNaN(value)) return '0.0';
    return Number(value).toFixed(decimals);
  };

  const escapeHtml = (unsafe) => {
    if (!unsafe) return '';
    return String(unsafe)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");
  };

  const formatTimestamp = (timestamp) => {
    try {
      let date;
      if (timestamp instanceof Date) date = timestamp;
      else if (typeof timestamp === 'string' && !isNaN(Date.parse(timestamp))) date = new Date(timestamp);
      else if (typeof timestamp === 'number') date = new Date(timestamp.toString().length === 10 ? timestamp * 1000 : timestamp);
      else date = new Date(timestamp);

      if (isNaN(date.getTime())) return 'Invalid date';

      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);

      const timeStr = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });

      if (date >= today) return `Last Updated Today ${timeStr}`;
      else if (date >= yesterday) return `Last Updated Yesterday ${timeStr}`;
      else return date.toLocaleDateString([], { year: 'numeric', month: '2-digit', day: '2-digit' }) + ' ' + timeStr;
    } catch (e) {
      return 'Unknown';
    }
  };

  const getMotorStatusDisplay = (lastMotorSwitch) => {
    try {
      if (!lastMotorSwitch) return 'No Data';
      if (lastMotorSwitch.direction === 'emergency' || lastMotorSwitch.motor_type === 'emergency' || lastMotorSwitch.state === 2) {
        return '⚠️ Emergency Stop';
      }
      if (lastMotorSwitch.direction && lastMotorSwitch.motor_type) {
        const motorType = (lastMotorSwitch.motor_type || '').toLowerCase();
        const direction = (lastMotorSwitch.direction || '').toLowerCase();
        switch (motorType) {
          case 'hoist': return `Hoist ${direction === 'up' ? 'Up' : direction === 'down' ? 'Down' : direction}`;
          case 'ct': return `CT ${direction === 'left' ? 'Left' : direction === 'right' ? 'Right' : direction}`;
          case 'lt': return `LT ${direction === 'forward' ? 'Forward' : direction === 'reverse' ? 'Reverse' : direction}`;
          default: return `${motorType.toUpperCase()} ${direction.charAt(0).toUpperCase() + direction.slice(1)}`;
        }
      } else if (lastMotorSwitch.state === 1) return 'Motor Active';
      else if (lastMotorSwitch.state === 0) return 'Idle';
      return 'Unknown';
    } catch (error) {
      return 'Unknown';
    }
  };

  const updateGaugeFill = (percentage) => {
    const circumference = 2 * Math.PI * 50;
    const validPercentage = Math.max(0, Math.min(100, percentage));
    return circumference - (validPercentage / 100) * circumference;
  };

  const getOEEColorClass = (value) => {
    if (value >= 85) return 'oee-excellent';
    if (value >= 75) return 'oee-good';
    if (value >= 65) return 'oee-fair';
    if (value > 0) return 'oee-poor';
    return '';
  };

  const normalizeStatus = (status) => {
    const lowerStatus = (status || '').toLowerCase();

    if (lowerStatus === 'offline' || lowerStatus === 'emergency') {
      return 'Off';
    }
    if (lowerStatus === 'working') {
      return 'Working';
    }
    if (lowerStatus === 'idle') {
      return 'Idle';
    }
    if (lowerStatus === 'overload') {
      return 'Overload';
    }
    if (lowerStatus === 'error') {
      return 'Error';
    }
    if (lowerStatus === 'off') {
      return 'Off';
    }
    return status || 'Off';
  };

  const getOperationTextColor = (status, isEmergencyStop = false) => {
    if (isEmergencyStop) {
      return '#dc3545';
    }

    const lowerStatus = (status || '').toLowerCase();

    switch (lowerStatus) {
      case 'working':
        return '#2ecc71'; // Green for working
      case 'idle':
        return '#f39c12'; // Orange/Yellow for idle
      case 'overload':
        return '#e74c3c'; // Red for overload
      case 'error':
        return '#9b59b6'; // Purple for error
      case 'off':
      case 'offline':
        return '#2c3e50'; // Dark gray for off
      default:
        return '#2c3e50'; // Default dark gray
    }
  };

  const getStatusBadgeClass = (status) => {
    const lowerStatus = (status || '').toLowerCase();
    switch (lowerStatus) {
      case 'working':
        return 'status-working';
      case 'idle':
        return 'status-idle';
      case 'off':
        return 'status-off';
      case 'overload':
        return 'status-overload';
      case 'error':
        return 'status-error';
      default:
        return 'status-off';
    }
  };

  // WebSocket handlers
  const initOEEWebSocket = useCallback(() => {
    try {
      const token = getAuthToken();
      const session_id = getSessionID();

      let host = BaseURL;
      if (BaseURL.includes('://')) {
        host = BaseURL.split('://')[1];
      }
      if (host.endsWith('/')) {
        host = host.slice(0, -1);
      }

      const wsScheme = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const wsUrl = `${wsScheme}//${host}/data/oee-dashboard/?token=${encodeURIComponent(token)}&session_id=${encodeURIComponent(session_id)}`;

      console.log('Connecting to OEE WebSocket:', wsUrl);

      oeeWebSocketRef.current = new WebSocket(wsUrl);

      oeeWebSocketRef.current.onopen = () => {
        console.log('OEE WebSocket connected successfully');
      };

      oeeWebSocketRef.current.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.type === 'pong') return;

          if (data.data && data.data.summary) {
            setOeeData({
              oee_percentage: data.data.summary.oee_percentage || 0,
              availability_percentage: data.data.summary.availability_percentage || 0,
              productivity_percentage: data.data.summary.productivity_percentage || 0,
              quality_percentage: data.data.summary.quality_percentage || 0
            });
          }
        } catch (error) {
          console.error('Error parsing OEE WebSocket message:', error);
        }
      };

      oeeWebSocketRef.current.onclose = () => {
        setTimeout(initOEEWebSocket, 5000);
      };

    } catch (error) {
      console.error('Error creating OEE WebSocket:', error);
    }
  }, []);

  const initAnalyticsWebSocket = useCallback(() => {
    try {
      const token = getAuthToken();
      const session_id = getSessionID();

      let host = BaseURL;
      if (BaseURL.includes('://')) {
        host = BaseURL.split('://')[1];
      }
      if (host.endsWith('/')) {
        host = host.slice(0, -1);
      }

      const wsScheme = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const wsUrl = `${wsScheme}//${host}/data/crane-analytics/?token=${encodeURIComponent(token)}&session_id=${encodeURIComponent(session_id)}`;

      console.log('Connecting to Analytics WebSocket:', wsUrl);

      analyticsWebSocketRef.current = new WebSocket(wsUrl);

      analyticsWebSocketRef.current.onopen = () => {
        console.log('Analytics WebSocket connected successfully');
      };

      analyticsWebSocketRef.current.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.type === 'pong') return;

          if (data.data && data.data.cranes) {
            const cranesMap = {};
            data.data.cranes.forEach(crane => {
              let rawStatus = crane.status_detailed || crane.status || 'Off';
              let normalizedStatus = normalizeStatus(rawStatus);
              if (normalizedStatus.toLowerCase() === 'offline') {
                normalizedStatus = 'Off';
              }

              craneLastDataTimestampRef.current[crane.crane_id] = crane.last_updated_time;
              cranesMap[crane.crane_id] = {
                id: crane.crane_id,
                name: crane.crane_name,
                crane_id: crane.crane_id,
                crane_name: crane.crane_name,
                line: crane.line,
                manufacturer: crane.manufacture,
                model: crane.model,
                safeWeightLoad: crane.capacity || 0,
                warningThreshold: crane.warning_threshold,
                upperThreshold: crane.upper_threshold,
                craneSpeed: crane.crane_speed,
                warningTemp: crane.warning_temp,
                criticalTemp: crane.critical_temp,
                status: normalizedStatus,
                status_detailed: normalizedStatus,
                deviceId: crane.device?.device_id || 'N/A',
                deviceName: crane.device?.device_name || 'N/A',
                currentLoad: crane.current_load || 0,
                power: crane.power || 0,
                currentPower: crane.power || 0,
                currentCurrent: crane.today_current || 0,
                health: crane.health_score || 0,
                healthStatus: crane.health_status || 'Unknown',
                capacity: crane.capacity || 0,
                last_motor_switch: crane.last_motor_switch,
                last_updated_time: crane.last_updated_time,
                connection_status: crane.connection_status || 'offline'
              };
            });
            setCraneData(cranesMap);

            if (data.data.summary) {
              setSummaryData(data.data.summary);
            }

            if (data.data.cranes) {
              setConnectionStatus({ cranes: data.data.cranes });
            }
          }
        } catch (error) {
          console.error('Error parsing Analytics WebSocket message:', error);
        }
      };

      analyticsWebSocketRef.current.onclose = () => {
        setTimeout(initAnalyticsWebSocket, 5000);
      };

    } catch (error) {
      console.error('Error creating Analytics WebSocket:', error);
    }
  }, []);

  // Filter cranes
  const filterCranes = (status) => {
    setCurrentFilter(status);
  };

  const getFilteredCranes = () => {
    if (currentFilter === 'all') return Object.values(craneData);
    return Object.values(craneData).filter(crane =>
      crane.status?.toLowerCase() === currentFilter.toLowerCase()
    );
  };

  // Create crane card
  const CraneCard = ({ crane }) => {
    const craneId = crane.crane_id || crane.id || 'unknown';
    const craneName = crane.crane_name || crane.name || 'Unnamed Crane';
    let statusDetailed = crane.status_detailed || crane.status || CRANE_STATUS.OFF;
    const connectionStatus = crane.connection_status || 'offline';
    const currentLoad = crane.current_load !== undefined ? crane.current_load : 0;
    const capacity = crane.capacity !== undefined ? crane.capacity : 0;
    const power = crane.power !== undefined ? crane.power : crane.currentPower || 0;

    const isEmergencyStop = crane.last_motor_switch?.direction === 'emergency' ||
      crane.last_motor_switch?.motor_type === 'emergency' ||
      crane.last_motor_switch?.state === 2 ||
      statusDetailed === 'EMERGENCY';

    const motorStatusDisplay = getMotorStatusDisplay(crane.last_motor_switch);
    let dataStatus;

    if (isEmergencyStop) {
      dataStatus = "off";
    } else {
      dataStatus = statusDetailed.toLowerCase().replace(/\s+/g, '-');
    }

    const isOnline = connectionStatus === 'online';
    const statusDotColor = isOnline ? '#10b981' : '#ef4444';
    const statusDotTooltip = isOnline ? 'Online' : 'Offline';

    const operationTextColor = getOperationTextColor(statusDetailed, isEmergencyStop);
    const statusBadgeClass = getStatusBadgeClass(statusDetailed);

    const handleCardClick = () => {
      console.log('Navigating with crane name:', crane.crane_name);
      console.log('Encoded name:', encodeURIComponent(crane.crane_name));
      console.log('Encoded id:', encodeURIComponent(crane.crane_id));

      navigate(`/subdashboard?crane=${encodeURIComponent(crane.crane_id)}&name=${encodeURIComponent(crane.crane_name)}&status=${encodeURIComponent(crane.status)}&machineId=${encodeURIComponent(crane.machineId || crane.crane_id)}`);
    };

    return (
      <div className={`status-card`} data-status={dataStatus} data-crane-id={craneId} onClick={handleCardClick}>
        <div className="status-card-header">
          <div>
            <div className="crane-name">
              <span
                className="status-dot"
                style={{ display: 'inline-block', width: '10px', height: '10px', borderRadius: '50%', backgroundColor: statusDotColor, marginRight: '8px', boxShadow: `0 0 4px ${statusDotColor}` }}
                title={statusDotTooltip}
              ></span>
              {escapeHtml(craneName)}
            </div>
            <div className="crane-id">ID: {escapeHtml(craneId)}</div>
          </div>
          <div className={`status-badge ${statusBadgeClass}`}>
            {escapeHtml(statusDetailed)}
          </div>
        </div>
        <div className="status-card-body">
          <div className="status-metric">
            <div className="metric-label">Current Load</div>
            <div className="metric-value">{formatNumber(currentLoad, 1)} kg</div>
          </div>
          <div className="status-metric">
            <div className="metric-label">Capacity</div>
            <div className="metric-value">{formatNumber(capacity, 1)} kg</div>
          </div>
          <div className="status-metric">
            <div className="metric-label">Power</div>
            <div className="metric-value">{formatNumber(power, 1)} kW</div>
          </div>
          <div className="status-metric">
            <div className="metric-label">{statusDetailed === 'Idle' || statusDetailed === 'IDLE' ? 'Last Operation' : 'Current Operation'}</div>
            <div
              className={`metric-value motor-status ${isEmergencyStop ? 'emergency' : ''}`}
              style={{ color: operationTextColor }}
            >
              {escapeHtml(motorStatusDisplay)}
            </div>
          </div>
        </div>
        <div className="status-card-footer">
          <div className="last-updated">
            {crane.last_updated_time ? formatTimestamp(crane.last_updated_time) : 'Never'}
          </div>
          <div className="view-details">
            <span>Details</span>
            <i className="fas fa-chevron-right"></i>
          </div>
        </div>
      </div>
    );
  };

  // Gauge Component
  const Gauge = ({ percentage, colorClass }) => {
    const offset = updateGaugeFill(percentage);
    return (
      <div className="metric-gauge">
        <svg className="gauge-svg" viewBox="0 0 120 120">
          <circle className="gauge-circle gauge-background" cx="60" cy="60" r="50" strokeWidth="10"></circle>
          <circle
            className="gauge-circle gauge-fill"
            cx="60" cy="60" r="50"
            strokeWidth="10"
            strokeDasharray="314"
            strokeDashoffset={offset}
            style={{ color: `var(${colorClass})` }}
          ></circle>
        </svg>
        <div className="gauge-text">
          <div className={`metric-value ${getOEEColorClass(percentage)}`}>{formatNumber(percentage, 1)}%</div>
        </div>
      </div>
    );
  };

  // Logout handler
  const handleLogout = () => {
    localStorage.removeItem('isLoggedIn');
    sessionStorage.removeItem('Token');
    sessionStorage.removeItem('session_id');
    navigate('/login');
  };

  // Refresh handlers
  const handleRefresh = () => {
    if (oeeWebSocketRef.current?.readyState === WebSocket.OPEN) {
      oeeWebSocketRef.current.send(JSON.stringify({ type: 'refresh' }));
    }
    if (analyticsWebSocketRef.current?.readyState === WebSocket.OPEN) {
      analyticsWebSocketRef.current.send(JSON.stringify({ type: 'refresh' }));
    }
  };

  // Initialize WebSockets on mount
  useEffect(() => {
    initOEEWebSocket();
    initAnalyticsWebSocket();

    return () => {
      if (oeeWebSocketRef.current) oeeWebSocketRef.current.close(1000, 'Component unmount');
      if (analyticsWebSocketRef.current) analyticsWebSocketRef.current.close(1000, 'Component unmount');
    };
  }, [initOEEWebSocket, initAnalyticsWebSocket]);

  // Get connection status summary
  const getConnectionStatusSummary = () => {
    const cranes = connectionStatus.cranes || [];
    const onlineCount = cranes.filter(c => c.connection_status === 'online').length;
    const totalCount = cranes.length;

    if (totalCount === 0) return { status: 'disconnected', message: 'No cranes available' };
    if (onlineCount === totalCount) return { status: 'connected', message: 'Connected' };
    if (onlineCount > 0) return { status: 'partial', message: `${onlineCount}/${totalCount} cranes Connected` };
    return { status: 'disconnected', message: 'Disconnected' };
  };

  const connectionSummary = getConnectionStatusSummary();
  const filteredCranes = getFilteredCranes();

  return (
    <div className="dashboard-content">
      {/* Header */}
      <div className="dashboard-header">
        <div className="header-left">
          <div className="header-logo">CraneIQ</div>
        </div>
        <div className="header-right">
          <div className="connection-status">
            <span className={`status-indicator ${connectionSummary.status}`}></span>
            <span className="status-text">{connectionSummary.message}</span>
          </div>
          <div className="user-info-wrapper">
            <div className="account-dropdown">
              <div className="account-trigger" onClick={() => setDropdownOpen(!dropdownOpen)}>
                {orgLogo ? (
                  <img
                    src={orgLogo}
                    alt="Organization Logo"
                    style={{
                      width: '32px',
                      height: '32px',
                      borderRadius: '50%',
                      objectFit: 'cover',
                      border: '2px solid #4361ee'
                    }}
                    onError={() => setOrgLogo(null)}
                  />
                ) : (
                  <FontAwesomeIcon icon={faUserCircle} style={{ fontSize: '1.5rem', color: '#4361ee' }} />
                )}
                <span>{sessionStorage.getItem('username') || 'User'}</span>
                <FontAwesomeIcon icon={faCaretDown} />
              </div>
              {dropdownOpen && (
                <div className="account-menu show">
                  <div className="account-menu-item plan-info">
                    <FontAwesomeIcon icon={faCrown} style={{ color: 'gold' }} />
                    <span>Basic Plan</span>
                  </div>
                  <div className="account-menu-divider"></div>
                  <button className="account-menu-item" style={{ background: 'none', border: 'none', width: '100%', textAlign: 'left', cursor: 'pointer' }}>
                    <FontAwesomeIcon icon={faUser} /> Profile
                  </button>
                  <button className="account-menu-item" style={{ background: 'none', border: 'none', width: '100%', textAlign: 'left', cursor: 'pointer' }}>
                    <FontAwesomeIcon icon={faSyncAlt} /> Upgrade Plan
                  </button>
                  <div className="account-menu-divider"></div>
                  <button className="account-menu-item logout-account-btn" onClick={handleLogout}>
                    <FontAwesomeIcon icon={faSignOutAlt} /> Logout
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Page Title */}
      <div className="page-titles">
        <h1>Dashboard Overview</h1>
        <p>Real-time view of all crane operations</p>
      </div>

      {/* Dashboard Stats Container */}
      <div className="dashboard-stats-container">
        {/* Quick Stats */}
        <div className="quickstats">
          <div className="statsheader">
            <div className="statstitle">Quick Stats</div>
            <div className="statsrefresh" onClick={handleRefresh}>
              <i className="fas fa-sync-alt"></i>
              <span>Refresh</span>
            </div>
          </div>
          <div className="statsgrid">
            <div className="statcard power">
              <div className="statlabel">
                <i className="fas fa-bolt"></i>
                <span>Today's Energy</span>
              </div>
              <div className="statvalue">{formatNumber(summaryData.motor_breakdown?.total_power_kwh || 0, 1)}</div>
              <div className="statunit">kWh</div>
              <div className="statchange">
                <i className="fas fa-calendar-day"></i>
                <span>Yesterday: {formatNumber(summaryData.yesterday?.total_power || 0, 1)} kWh</span>
              </div>
            </div>
            <div className="statcard current">
              <div className="statlabel">
                <i className="fas fa-rupee-sign"></i>
                <span>Today's Cost</span>
              </div>
              <div className="statvalue">{formatNumber(summaryData.motor_breakdown?.total_energy_cost || 0, 1)}</div>
              <div className="statunit">Rs</div>
              <div className="statchange">
                <i className="fas fa-calendar-day"></i>
                <span>Yesterday: {formatNumber(summaryData.yesterday?.total_cost || 0, 1)} Rs</span>
              </div>
            </div>
            <div className="statcard active">
              <div className="statlabel">
                <i className="fas fa-cogs"></i>
                <span>Active Cranes</span>
              </div>
              <div className="statvalue">{summaryData.active_cranes || 0}</div>
              <div className="statunit">/{summaryData.total_cranes || 0} total</div>
              <div className="statchange positive">
                <i className="fas fa-arrow-up"></i>
                <span>{summaryData.total_cranes ? Math.round((summaryData.active_cranes / summaryData.total_cranes) * 100) : 0}% utilization</span>
              </div>
            </div>
            <div className="statcard idle">
              <div className="statlabel">
                <i className="fas fa-pause"></i>
                <span>Idle Cranes</span>
              </div>
              <div className="statvalue">{summaryData.idle_cranes || 0}</div>
              <div className="statunit">/{summaryData.total_cranes || 0} total</div>
              <div className="statchange negative">
                <i className="fas fa-arrow-down"></i>
                <span>{summaryData.total_cranes ? Math.round((summaryData.idle_cranes / summaryData.total_cranes) * 100) : 0}% utilization</span>
              </div>
            </div>
          </div>
        </div>

        {/* OEE Section */}
        <div className="oeecontainer">
          <div className="oeeheader">
            <div className="oeetitle">
              <i className="fas fa-chart-pie"></i>
              <h3>Overall Equipment Effectiveness</h3>
            </div>
          </div>
          <div className="oeemetrics-row">
            <div className="oeemetric-card oee">
              <Gauge percentage={oeeData.oee_percentage} colorClass="--oee-color" />
              <div className="metriclabel">OEE</div>
              <div className="metricdescription">Overall equipment effectiveness</div>
            </div>
            <div className="oeemetric-card availability">
              <Gauge percentage={oeeData.availability_percentage} colorClass="--availability-color" />
              <div className="metriclabel">Availability</div>
              <div className="metricdescription">Uptime vs planned production</div>
            </div>
            <div className="oeemetric-card performance">
              <Gauge percentage={oeeData.productivity_percentage} colorClass="--performance-color" />
              <div className="metriclabel">Productivity</div>
              <div className="metricdescription">Speed vs ideal cycle time</div>
            </div>
          </div>
        </div>
      </div>

      {/* Status Filter Bar */}
      <div className="status-filter-bar">
        <div className={`status-filter all ${currentFilter === 'all' ? 'active' : ''}`} onClick={() => filterCranes('all')}>
          <i className="fas fa-list"></i>
          <span>All Cranes</span>
        </div>
        <div className={`status-filter working ${currentFilter === 'working' ? 'active' : ''}`} onClick={() => filterCranes('working')}>
          <i className="fas fa-hard-hat"></i>
          <span>Working</span>
        </div>
        <div className={`status-filter idle ${currentFilter === 'idle' ? 'active' : ''}`} onClick={() => filterCranes('idle')}>
          <i className="fas fa-pause-circle"></i>
          <span>Idle</span>
        </div>
        <div className={`status-filter off ${currentFilter === 'off' ? 'active' : ''}`} onClick={() => filterCranes('off')}>
          <i className="fas fa-power-off"></i>
          <span>Off</span>
        </div>
        <div className={`status-filter overload ${currentFilter === 'overload' ? 'active' : ''}`} onClick={() => filterCranes('overload')}>
          <i className="fas fa-weight-hanging"></i>
          <span>Overload</span>
        </div>
        <div className={`status-filter error ${currentFilter === 'error' ? 'active' : ''}`} onClick={() => filterCranes('error')}>
          <i className="fas fa-exclamation-triangle"></i>
          <span>Errors</span>
        </div>
      </div>

      {/* Status Grid */}
      <div className="status-grid">
        {Object.keys(craneData).length === 0 ? (
          <div className="loading-message">
            <i className="fas fa-spinner fa-spin"></i>
            <span>Loading crane data...</span>
          </div>
        ) : (
          filteredCranes.map(crane => <CraneCard key={crane.crane_id} crane={crane} />)
        )}
      </div>
    </div>
  );
};

export default CraneDashboard;